<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BSidebar
      id="sbRsmLead"
      backdrop-variant="dark"
      backdrop
      shadow
      right
    >
      <template #title>
        <i class="fad fa-filter-list" /> Filters
      </template>

      <BContainer>
        <BRow>
          <BCol
            cols="12"
          >
            <BFormGroup
              label="Cluster"
              label-for="filterCluster"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <VSelect
                v-model="tableLeads.selected.cluster"
                placeholder="filter cluster here"
                input-id="filterCluster"
                label="cluster_name"
                class="border border-light rounded-lg"
                clearable
                append-to-body
                :options="(tableLeads.options.clusters)"
                :loading="(tableLeads.fetching.clusters)"
                :disabled="(tableLeads.busy || tableLeads.fetching.clusters)"
                :calculate-position="calculateDropPosition"
                @search="searchFilterClusters"
              >
                <template #no-options="">
                  no available cluster
                </template>
              </VSelect>
            </BFormGroup>
          </BCol>
        </BRow>
      </BContainer>
    </BSidebar>

    <BRow
      class="py-2"
    >
      <BCol
        cols="12"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BInputGroup>
            <BFormInput
              id="filterSearch"
              v-model="tableLeads.filter.search"
              placeholder="search here"
              autocomplete="off"
              debounce="1000"
              type="text"
            />
            <BInputGroupAppend>
              <BButton
                v-b-toggle.sbRsmLead
                variant="primary"
              >
                <i
                  v-if="[
                    settingFilters,
                    tableLeads.fetching.clusters
                  ].some(isTrue => isTrue)"
                  class="fad fa-spinner fa-spin p-0"
                />
                <i
                  v-else
                  class="fad fa-filter-list"
                /> Filters
              </BButton>
            </BInputGroupAppend>
          </BInputGroup>
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableLeads"
      :provider="tableProvider"
      :fields="tableLeads.fields"
      :filter="tableLeads.filter"
      :filter-on="tableLeads.filterOn"
      :sort-by="tableLeads.sortBy"
      :sort-desc="tableLeads.sortDesc"
      :sort-direction="tableLeads.sortDirection"
      :total-rows="tableLeads.totalRows"
      :current-page="tableLeads.currentPage"
      :per-page="tableLeads.perPage"
      :stacked="tableLeads.stacked"
      :busy="tableLeads.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableLeads.busy"
            :to="{
              name: 'asm.leads.profile',
              params: {
                lead: item.id
              }
            }"
          >
            Manage
          </BButton>
        </div>
      </template>

      <template #cell(lead_name)="{ item }">
        <div class="d-flex flex-column gap-1 p-1">
          <div class="font-weight-bolder">
            {{ item.lead_name }}
          </div>
          <div style="font-size: 10px;">
            {{ item.address }}
          </div>
        </div>
      </template>
    </CustomTable>

  </BContainer>
</template>
<script>
import QUERY from 'lodash'
import { mapGetters } from 'vuex'
import { setTableOptions, getTableOptions } from '@/forage/asm/leads'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MxImageUrl from '@/mixins/image-url'
import MClusters from '@/mixins/locations/clusters'

import { SASMLead } from '@/services/asm'

export default {

  name: 'VASMLeads',

  mixins: [
    MClusters,
    MxMisc,
    MxFormatter,
    MxImageUrl
  ],

  beforeRouteEnter (_to, _from, navigate) {
    getTableOptions().then(options => {
      navigate(context => {
        if (context.currentUser) {
          context.tableLeads.selected.area = {
            id: context.currentUser?.area || ''
          }

          if (options) {
            const { currentPage, perPage, sortBy, sortDesc, totalRows, filter } = options

            context.tableLeads = Object.assign(context.tableLeads, {
              totalRows: totalRows || 0,
              sortBy: sortBy || 'id',
              sortDesc: sortDesc || false,
              perPage: perPage || 10,
              currentPage: currentPage || 0,
              filter: {
                search: filter?.search || '',
                cluster: ''
              },
              editable: {
                cluster: filter?.cluster || ''
              }
            })
          } else {
            context.tableLeads.currentPage = 1
          }

          context.$nextTick(() => {
            context.getClusters(context.tableLeads, '', context.currentUser.area).finally(() => {
              if (context.tableLeads.editable.cluster) {
                context.tableLeads.selected.cluster = QUERY.find(context.tableLeads.options.clusters, {
                  id: context.tableLeads.editable.cluster
                }) || ''

                context.tableLeads.editable.cluster = ''
              }
            })
          })
        }
      })
    })
  },

  middleware: [
    'auth',
    'asm'
  ],

  metaInfo: () => ({
    title: 'Leads'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false,
        settings: false
      },
      tableLeads: {
        busy: false,
        fetching: {
          clusters: false
        },
        options: {
          yesOrNos: [
            { value: 'All', text: 'All' },
            { value: 1, text: 'Yes' },
            { value: 0, text: 'No' }
          ],
          clusters: []
        },
        editable: {
          cluster: ''
        },
        selected: {
          tagging: 1,
          area: {
            id: ''
          },
          cluster: ''
        },
        filter: {
          search: '',
          cluster: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 0,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-right stacked-full text-md-center' },
          { mobile: 7, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'lead_name', label: 'Lead', class: 'min-w-md-250px', formatter: this.getFullName, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    settingFilters () {
      const hasEditables = Object.keys(this.tableLeads.editable).map(
        editableKey => this.tableLeads.editable[editableKey]
      ).some(val => String(val) !== '')

      return hasEditables
    }
  },

  watch: {
    'tableLeads.selected.cluster' (cluster) {
      this.tableLeads.filter.cluster = cluster?.id || ''
    }
  },

  methods: {
    leadStatus (status) {
      switch (status) {
        case 'Verified': {
          return 'secondary'
        }
        case 'New': {
          return 'primary'
        }
        default: {
          return 'warning'
        }
      }
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      if (this.settingFilters) {
        return []
      }

      this.tableLeads.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        area_id: this.currentUser.area,
        cluster_id: filter.cluster
      }

      return await SASMLead.get(payload).then(
        ({ data }) => {
          this.tableLeads.totalRows = data.total_rows
          setTableOptions({
            currentPage,
            perPage,
            sortBy,
            sortDesc,
            filter,
            totalRows: data.total_rows
          })
          return data.items
        }
      ).catch(
        () => {
          this.tableLeads.totalRows = 0
          setTableOptions({
            currentPage,
            perPage,
            sortBy,
            sortDesc,
            filter,
            totalRows: 0
          })
          return []
        }
      ).finally(() => {
        this.tableLeads.busy = false
      })
    },

    async searchFilterClusters (search, loading) {
      this.searchClustersProvider(this.tableLeads, search, loading)
    }
  }
}
</script>
